import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './ServiceProvider.scss';
import { CompactHeader } from '../../components/CompactHeader/CompactHeader';
import { CompactFooter } from '../../components/CompactFooter/CompactFooter';
import { FiCheck } from 'react-icons/fi';

const CheckIcon = FiCheck as any;

/**
 * Ported from `service-provider/register-thank-you.blade.php`, with one addition: a newly
 * provisioned Client Executive Agent is offered "Set Your Password" here, exactly as the
 * investor success screen does. Without it the emailed link was their only way in, and the
 * "Go to Login" button sent them to B2C before they had a password.
 *
 * The id arrives in router state and is present only for a brand-new agent. Every other
 * provider type receives a B2B invite and has no Facilon password to set, so they keep the
 * original buttons — as does an agent whose login already existed, or anyone who refreshes
 * this page.
 */
const SpThankYou: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { azureUserId?: string; loginStatus?: string } | null;
  const azureUserId = state?.azureUserId;
  // Explicit outcome from the API. Inferring it from a missing id conflated "already has a
  // login" with "login could not be created" and showed the provider wording for both.
  const loginStatus = state?.loginStatus;
  const canSetPassword = loginStatus === 'PASSWORD_PENDING' && !!azureUserId;
  const existingLogin = loginStatus === 'EXISTING_LOGIN';
  const loginFailed = loginStatus === 'LOGIN_FAILED';

  return (
    <>
      <CompactHeader />
      <section className="login-form-style4 section-padding py-12 flex justify-center items-center min-h-[60vh]">
        <div className="container flex justify-center">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl border border-slate-100 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
              <CheckIcon size={32} className="text-emerald-600 flex-shrink-0" />
            </div>

            <h3 className="text-[22px] font-bold text-slate-800 mb-2">Thank You!</h3>

            <div className="text-[12px] text-slate-500 mb-4 max-w-[280px] leading-relaxed">
              {canSetPassword ? (
                <>
                  <p className="mb-2">Your registration has been submitted successfully.</p>
                  <p className="mb-2">Set your password below to activate your account and sign in.</p>
                  <p className="mb-0 text-[11px] opacity-80">
                    (We have emailed you the same link — check your <strong>Spam</strong> or{' '}
                    <strong>Junk</strong> folder if it does not arrive.)
                  </p>
                </>
              ) : existingLogin ? (
                <>
                  <p className="mb-2">Your registration has been submitted successfully.</p>
                  <p className="mb-2">
                    You already have a <strong>Facilon account</strong>. Sign in with your existing
                    password to open the Client Executive Agent console.
                  </p>
                  <p className="mb-0 text-[11px] opacity-80">
                    (Forgotten it? Use <strong>Forgot password</strong> on the login page.)
                  </p>
                </>
              ) : loginFailed ? (
                <>
                  <p className="mb-2">Your registration has been saved.</p>
                  <p className="mb-2">
                    Your login could not be activated just now, so you cannot sign in yet.
                  </p>
                  <p className="mb-0 text-[11px] opacity-80">
                    Please contact <strong>Facilon support</strong> and we will complete it for you.
                  </p>
                </>
              ) : (
                <>
                  <p className="mb-2">Your details have been submitted successfully.</p>
                  <p className="mb-2">
                    The <strong>Facilon Team</strong> will get back to you shortly.
                  </p>
                  <p className="mb-0 text-[11px] opacity-80">
                    (Please check your <strong>Inbox</strong>. Kindly check your{' '}
                    <strong>Spam</strong> or <strong>Junk</strong> folder.)
                  </p>
                </>
              )}
            </div>

            <div className="w-full flex flex-col gap-2">
              {canSetPassword ? (
                <button
                  type="button"
                  className="w-full py-2 bg-[#3e6f7c] hover:bg-[#355f69] text-white text-[12px] font-bold rounded-md transition-colors cursor-pointer border-0 shadow-sm"
                  onClick={() => navigate(`/investor/setpassword/${azureUserId}`)}
                >
                  Set Your Password
                </button>
              ) : loginFailed ? null : (
                <button
                  type="button"
                  className="w-full py-2 bg-[#3e6f7c] hover:bg-[#355f69] text-white text-[12px] font-bold rounded-md transition-colors cursor-pointer border-0 shadow-sm"
                  onClick={() => navigate('/login')}
                >
                  Go to Login
                </button>
              )}
              <button 
                type="button"
                className="w-full py-2 bg-[#f8fafc] hover:bg-slate-100 text-slate-600 text-[12px] font-bold rounded-md transition-colors cursor-pointer border border-slate-200 shadow-sm" 
                onClick={() => navigate('/')}
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </section>
      <CompactFooter />
    </>
  );
};

export default SpThankYou;
